import React from 'react';
import { Timestamp } from 'firebase/firestore';

interface Conversation {
  userId: string;
  phone: string;
  userName: string;
  lastMessage: string;
  lastMessageTime: Timestamp | Date | string | null;
  unreadCount: number;
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedUserId: string | null;
  onSelectConversation: (userId: string) => void;
}

// Helper to format timestamp - handles all Firestore timestamp formats
const formatMessageTime = (time: Timestamp | Date | string | null): string => {
  if (!time) return 'No date';
  
  try {
    let date: Date;
    
    // Handle Firestore Timestamp objects with toDate() method
    if (time && typeof time === 'object' && 'toDate' in time) {
      date = (time as Timestamp).toDate();
    } 
    // Handle Firestore's internal format (_seconds and _nanoseconds)
    else if (time && typeof time === 'object' && '_seconds' in time) {
      const firebaseTimestamp = time as any;
      date = new Date(firebaseTimestamp._seconds * 1000);
    }
    // Handle plain Date objects
    else if (time instanceof Date) {
      date = time;
    } 
    // Handle ISO strings and numeric timestamps
    else if (typeof time === 'string' || typeof time === 'number') {
      date = new Date(time);
    } 
    // Unknown format - log and return fallback
    else {
      console.warn('Unknown timestamp format received:', time);
      return 'Just now';
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date after parsing:', time);
      return 'Just now';
    }
    
    // Calculate time difference
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    // Return formatted relative time
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    
  } catch (error) {
    console.error('Error formatting timestamp:', error, 'Input was:', time);
    return 'Just now';
  }
};

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedUserId,
  onSelectConversation,
}) => {
  return (
    <div>
      {conversations && conversations.length > 0 ? (
        conversations.map((conv) => (
          <div
            key={conv.userId}
            onClick={() => onSelectConversation(conv.userId)}
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #e0e0e0',
              cursor: 'pointer',
              backgroundColor: selectedUserId === conv.userId ? '#f0f0f0' : 'white',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              if (selectedUserId !== conv.userId) {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedUserId !== conv.userId) {
                e.currentTarget.style.backgroundColor = 'white';
              }
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontWeight: conv.unreadCount > 0 ? 'bold' : 'normal',
                  fontSize: '16px',
                  marginBottom: '4px',
                }}>
                  {conv.userName && conv.userName !== 'Unknown' 
                    ? conv.userName 
                    : (conv.phone || 'Unknown')}
                </div>
                <div style={{ 
                  fontSize: '14px',
                  color: '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {conv.lastMessage && conv.lastMessage !== '[Message]'
                    ? conv.lastMessage
                    : '[No messages yet]'}
                </div>
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#999',
                marginLeft: '8px',
                whiteSpace: 'nowrap',
              }}>
                {/* ✅ Safe rendering of timestamp */}
                {formatMessageTime(conv.lastMessageTime)}
              </div>
            </div>
            {conv.unreadCount > 0 && (
              <div style={{
                marginTop: '4px',
                display: 'inline-block',
                backgroundColor: '#25D366',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px',
                fontWeight: 'bold',
              }}>
                {conv.unreadCount}
              </div>
            )}
          </div>
        ))
      ) : (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
          No conversations yet
        </div>
      )}
    </div>
  );
};
